/**
 * inbox-shared — display helpers shared by the Needs-You and Notifications
 * lanes (Inbox V2, spec §4.2). ADR-057: monochrome by default; the only colour
 * is the tiny sanctioned intent glyph, always via a semantic token (never raw
 * hex), AA-tuned in both themes (tokens.css defines a dark variant for each).
 *
 * It also exposes the deep-link target for a row (§4.1) and a cached breadcrumb
 * hook (`useCardScope`) that resolves "Org · Project · #Card" from the card /
 * board / project caches — bounded because Needs You is small and Notifications
 * is virtualized (only visible rows resolve).
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Bell,
  CircleCheck,
  Hand,
  KeyRound,
  MessageCircleQuestion,
  ShieldQuestion,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';

import { useActiveOrg } from '../../lib/active-org';
import { boardsApi, boardsKeys } from '../../lib/api/boards';
import { kanbanApi, kanbanKeys } from '../../lib/api/kanban';
import type { InboxIntent, Mention } from '../../lib/api/mentions';
import { projectsApi, projectKeys } from '../../lib/api/projects';

interface IntentDisplay {
  /** lucide glyph for the row. */
  Icon: LucideIcon;
  /** Tailwind text-colour class bound to a semantic token (AA both themes). */
  colorClass: string;
  /** Short label for tooltips / a11y. */
  label: string;
}

/**
 * Intent → glyph + colour. Per spec §4.2:
 *   ask          → MessageCircleQuestion, text-foreground (monochrome)
 *   approval     → ShieldQuestion,        text-warning (decision pending)
 *   blocker      → Hand (raised hand),    text-info (waiting for your reply)
 *   for_approval → CircleCheck,           text-foreground (accept/reject gate)
 * Notification intents (progress/done/mention) render no loud glyph — the lane
 * uses a terse "·" bullet — but we keep neutral entries so the map is total.
 */
const INTENT_DISPLAY: Record<InboxIntent, IntentDisplay> = {
  ask: { Icon: MessageCircleQuestion, colorClass: 'text-foreground', label: 'Question' },
  approval: { Icon: ShieldQuestion, colorClass: 'text-warning', label: 'Approval' },
  blocker: { Icon: Hand, colorClass: 'text-info', label: 'Blocked' },
  for_approval: { Icon: CircleCheck, colorClass: 'text-foreground', label: 'For approval' },
  // access_request → a blocked agent needs a grant/connect; key glyph, warning
  // tone (a decision the founder must make to unblock work).
  access_request: { Icon: KeyRound, colorClass: 'text-warning', label: 'Access request' },
  progress: { Icon: Bell, colorClass: 'text-muted-foreground', label: 'Update' },
  done: { Icon: CircleCheck, colorClass: 'text-muted-foreground', label: 'Done' },
  mention: { Icon: Bell, colorClass: 'text-muted-foreground', label: 'Mention' },
};

export function intentDisplay(intent: InboxIntent | null): IntentDisplay {
  return (intent && INTENT_DISPLAY[intent]) || INTENT_DISPLAY.mention;
}

/**
 * The deep-link target for a Needs-You / Notification row. `intent='ask'` rows
 * carry `?ask=<askId>` so the AskPopup (built in a later phase) auto-opens;
 * `intent='approval'` rows carry `?approval=<eventId>` (+ `?asid=<sessionId>`)
 * so the ApprovalPopup auto-opens. Both params are harmless until then. Rows
 * without a card have no target.
 */
export type InboxLinkTarget =
  | {
      to: '/board/$boardId/card/$cardId';
      params: { boardId: string; cardId: string };
      search?: { ask?: string; approval?: string; asid?: string; accessRequest?: string };
    }
  // Role Continuous Learning (0112) — a proposed role improvement deep-links to
  // Role Settings → Training, NOT the card. `role`=the role being improved,
  // `improvement`=the addition id; the roles route opens the drawer focused on it.
  | {
      to: '/company/roles';
      search: { role: string; improvement: string };
    }
  // Scoped Knowledge Routing (0113) — a proposed knowledge addition deep-links
  // to Settings → Knowledge, NOT the card. `kscope`=project|org, `ktarget`=the
  // project/board or org id, `knowledge`=the addition id; the knowledge route
  // opens the matching tab focused on that proposal.
  | {
      to: '/company/knowledge';
      search: { kscope: 'project' | 'org'; ktarget: string; knowledge: string };
    };

export async function resolveInboxLink(m: Mention): Promise<InboxLinkTarget | null> {
  // role_addition rows carry the addition id in source_id and the role being
  // improved in by_role_id (the proposing agent runs AS that role). Route to the
  // Training tab instead of the card. `sourceKind` isn't in the UI union yet, so
  // compare as string. Needs both ids to focus the drawer; else fall through.
  if ((m.sourceKind as string) === 'role_addition') {
    if (m.byRoleId && m.sourceId) {
      return {
        to: '/company/roles',
        search: { role: m.byRoleId, improvement: m.sourceId },
      };
    }
    return null;
  }
  // knowledge_addition rows carry the addition id in source_id and the scope +
  // target (org id / project-board id) in knowledgeScope/knowledgeTargetId
  // (set by the gateway mapper). Route to the Knowledge settings tab, not the
  // card. Needs both scope + target to focus; else fall through.
  if ((m.sourceKind as string) === 'knowledge_addition') {
    if (m.knowledgeScope && m.knowledgeTargetId && m.sourceId) {
      return {
        to: '/company/knowledge',
        search: {
          kscope: m.knowledgeScope,
          ktarget: m.knowledgeTargetId,
          knowledge: m.sourceId,
        },
      };
    }
    return null;
  }
  if (!m.cardId) return null;
  let card;
  try {
    card = await kanbanApi.getCard(m.cardId);
  } catch {
    return null;
  }
  if (!card?.boardId) return null;
  const search: { ask?: string; approval?: string; asid?: string; accessRequest?: string } = {};
  if (m.intent === 'ask' && m.askId) search.ask = m.askId;
  if (m.intent === 'approval' && m.sourceId) {
    search.approval = m.sourceId;
    if (m.sessionId) search.asid = m.sessionId;
  }
  // access_request → the row's source_id is the chaos.access_requests id; the
  // AccessRequestPopup auto-opens from ?accessRequest=<id>.
  if (m.intent === 'access_request' && m.sourceId) search.accessRequest = m.sourceId;
  return {
    to: '/board/$boardId/card/$cardId',
    params: { boardId: card.boardId, cardId: card.id },
    ...(Object.keys(search).length ? { search } : {}),
  };
}

export interface CardScope {
  orgName: string | null;
  projectName: string | null;
  cardTitle: string | null;
  loading: boolean;
}

/**
 * Resolve "Org · Project · #Card" for an inbox row, all from react-query caches
 * (shared across rows, so N rows for the same card cost one fetch). Org name
 * comes from the active-org context; project + card titles are lazily fetched
 * and cached. Enabled-gated so virtualized off-screen rows don't fetch.
 */
export function useCardScope(
  cardId: string | null,
  orgId?: string | null,
  enabled = true,
): CardScope {
  const { activeOrg, orgs } = useActiveOrg();

  const cardQ = useQuery({
    queryKey: cardId ? kanbanKeys.card(cardId) : ['inbox', 'card', 'idle'],
    queryFn: () => kanbanApi.getCard(cardId as string),
    enabled: enabled && !!cardId,
    staleTime: 60_000,
  });

  const boardId = cardQ.data?.boardId ?? null;
  const boardQ = useQuery({
    queryKey: boardId ? boardsKeys.detail(boardId) : ['inbox', 'board', 'idle'],
    queryFn: () => boardsApi.get(boardId as string),
    enabled: enabled && !!boardId,
    staleTime: 60_000,
  });

  const projectId = boardQ.data?.projectId ?? null;
  const projectsQ = useQuery({
    queryKey: projectKeys.list(),
    queryFn: () => projectsApi.list(),
    enabled: enabled && !!projectId,
    staleTime: 60_000,
  });

  const projectName =
    projectId && projectsQ.data
      ? projectsQ.data.find((p) => p.id === projectId)?.name ?? null
      : null;

  // Cross-org Inbox: the row's org is the ITEM's org (via `orgId`), NOT the
  // active org — a global Inbox spans many orgs. Resolve its display name from
  // the orgs list; fall back to the active org only when no orgId was passed
  // (legacy per-org callers).
  const orgName =
    (orgId ? orgs.find((o) => o.id === orgId)?.displayName ?? null : null) ??
    activeOrg?.displayName ??
    null;

  return {
    orgName,
    projectName,
    cardTitle: cardQ.data?.title ?? null,
    loading: cardQ.isLoading || boardQ.isLoading,
  };
}

/**
 * useOrgLabel — the display name for an org id, resolved from the orgs list the
 * org switcher already loads (system founders get every org; members get their
 * memberships). Returns null while the list is loading or the org is unknown.
 * Used to badge each Inbox row with its owning org (the Inbox is cross-org).
 */
export function useOrgLabel(orgId: string | null | undefined): string | null {
  const { orgs } = useActiveOrg();
  if (!orgId) return null;
  return orgs.find((o) => o.id === orgId)?.displayName ?? null;
}

/**
 * useInboxNavigate — navigate to a card that may live in a NON-active org.
 *
 * The Inbox is global, so a row can point at a card in an org that is not the
 * currently-active one. The board/card route renders from `activeOrgId`, so we
 * MUST switch the active org to the card's org BEFORE navigating, otherwise the
 * board mounts against the wrong org and the card fails to open. This helper
 * does that switch (only when the org differs) and then navigates.
 *
 * `orgId` is the card's owning org (from the inbox item). Passing null/undefined
 * (or the already-active org) skips the switch and just navigates.
 */
export function useInboxNavigate(): (
  target: InboxLinkTarget,
  orgId?: string | null,
) => Promise<void> {
  const navigate = useNavigate();
  const { activeOrgId, setActiveOrg } = useActiveOrg();
  return React.useCallback(
    async (target: InboxLinkTarget, orgId?: string | null): Promise<void> => {
      // Switch org context first so the destination board opens in the right
      // org (the Inbox spans orgs; the board route is per-active-org).
      if (orgId && orgId !== activeOrgId) {
        setActiveOrg(orgId);
      }
      try {
        await navigate(target as never);
      } catch {
        /* ignore navigation race */
      }
    },
    [navigate, activeOrgId, setActiveOrg],
  );
}
